/* ==========================================================================
   <tint-mockup> — the framed-artwork slideshow on the homepage wall
   --------------------------------------------------------------------------
   The server renders every piece, stacked, with the first one carrying
   `is-active` and its title and price already in the caption. With JavaScript
   off that first piece is simply what you see: a real framed artwork on the
   wall, captioned, linked to its product page. Everything below is
   enhancement, and none of it moves the layout on hydration.

   Autoplay is deliberately timid. It stops on hover, on focus within, when
   the tab is hidden, and permanently the moment a visitor touches any
   control — a hero that keeps moving while someone is reading it is worse
   than one that never moved at all. Under prefers-reduced-motion it never
   starts.

   Hidden pieces are made `inert` rather than only tabindex="-1": inert takes
   the whole subtree out of the accessibility tree and out of find-in-page,
   which is what you actually want for a slide nobody can see.
   ========================================================================== */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const SUPPORTS_INERT = 'inert' in HTMLElement.prototype;

class TintMockup extends HTMLElement {
  connectedCallback() {
    this.pieces = Array.from(this.querySelectorAll('[data-piece]'));
    if (this.pieces.length === 0) return;

    this.index = Math.max(0, this.pieces.findIndex((p) => p.classList.contains('is-active')));
    this.interval = Math.max(3000, parseInt(this.dataset.interval, 10) || 7000);
    this.wantsAuto = this.dataset.autoplay === 'true' && this.pieces.length > 1;
    this.surrendered = false;
    this.paused = false;

    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.metaTitle = this.querySelector('[data-meta-title]');
    this.metaPrice = this.querySelector('[data-meta-price]');
    this.progress = this.querySelector('[data-progress]');
    this.pauseBtn = this.querySelector('[data-pause]');

    this.bind();
    this.sync();

    if (this.wantsAuto && !REDUCED.matches) this.start();

    this.onVisibility = () => (document.hidden ? this.halt() : this.resume());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.addEventListener('pointerenter', () => this.halt());
    this.addEventListener('pointerleave', () => this.resume());
    this.addEventListener('focusin', () => this.halt());
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.resume();
    });

    if (REDUCED.addEventListener) {
      REDUCED.addEventListener('change', () => (REDUCED.matches ? this.halt() : this.resume()));
    }
  }

  disconnectedCallback() {
    this.halt();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  bind() {
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { this.surrender(); this.goTo(i); });
    });

    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    if (prev) prev.addEventListener('click', () => { this.surrender(); this.goTo(this.index - 1); });
    if (next) next.addEventListener('click', () => { this.surrender(); this.goTo(this.index + 1); });

    if (this.pauseBtn) {
      this.pauseBtn.addEventListener('click', () => {
        this.paused = !this.paused;
        this.pauseBtn.setAttribute('aria-pressed', String(this.paused));
        this.pauseBtn.dataset.state = this.paused ? 'paused' : 'playing';
        this.pauseBtn.setAttribute('aria-label', this.paused ? 'Play the slideshow' : 'Pause the slideshow');
        if (this.paused) { this.halt(); } else { this.resume(); }
      });
    }

    this.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      this.surrender();
      this.goTo(this.index + (e.key === 'ArrowRight' ? 1 : -1));
    });
  }

  /* A visitor took control. Stop advancing on our own for the rest of the visit. */
  surrender() {
    this.surrendered = true;
    this.halt();
    if (this.progress) this.progress.hidden = true;
  }

  start() {
    this.halt();
    if (this.surrendered || this.paused || REDUCED.matches) return;
    this.restartProgress();
    this.timer = window.setInterval(() => this.goTo(this.index + 1, true), this.interval);
  }

  halt() {
    if (this.timer) { window.clearInterval(this.timer); this.timer = null; }
    if (this.progress) this.progress.style.animationPlayState = 'paused';
  }

  resume() {
    if (this.wantsAuto && !this.surrendered && !this.paused && !document.hidden && !REDUCED.matches) {
      this.start();
    }
  }

  restartProgress() {
    if (!this.progress) return;
    this.progress.style.animation = 'none';
    void this.progress.offsetWidth;
    this.progress.style.animation = '';
    this.progress.style.animationDuration = this.interval + 'ms';
    this.progress.style.animationPlayState = 'running';
  }

  goTo(next, auto) {
    const count = this.pieces.length;
    const i = ((next % count) + count) % count;
    if (i === this.index) return;

    this.index = i;
    this.sync();

    if (!auto) { this.halt(); } else { this.restartProgress(); }
  }

  sync() {
    this.pieces.forEach((piece, i) => {
      const on = i === this.index;
      piece.classList.toggle('is-active', on);

      if (SUPPORTS_INERT) {
        piece.inert = !on;
        piece.removeAttribute('aria-hidden');
      } else {
        piece.setAttribute('aria-hidden', String(!on));
        piece.tabIndex = on ? 0 : -1;
      }

      /* Load the one coming next before it is needed, but never on first
         paint — the active image is the LCP element and gets the bandwidth. */
      const img = piece.querySelector('img');
      if (img && i === (this.index + 1) % this.pieces.length) img.loading = 'eager';
    });

    this.dots.forEach((dot, i) => {
      const on = i === this.index;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-current', on ? 'true' : 'false');
    });

    const active = this.pieces[this.index];
    if (!active) return;
    if (this.metaTitle) this.metaTitle.textContent = active.dataset.title || '';
    if (this.metaPrice) this.metaPrice.textContent = active.dataset.price || '';
  }
}

if (!customElements.get('tint-mockup')) {
  customElements.define('tint-mockup', TintMockup);
}
